package com.bikompanion.data.parking

import kotlinx.coroutines.TimeoutCancellationException
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Semaphore
import kotlinx.coroutines.sync.withPermit
import kotlinx.coroutines.withTimeout
import kotlinx.coroutines.withTimeoutOrNull
import com.bikompanion.data.OverpassEndpoints
import kotlin.coroutines.cancellation.CancellationException
import kotlin.time.Duration
import kotlin.time.Duration.Companion.seconds

/** How long the leading instance gets to itself before the next one is started alongside it. */
private val HedgeDelay = 4.seconds

/** The most instances being asked at once, so hedging can't turn into a broadcast. */
private const val MaxConcurrentAttempts = 3

/**
 * Everything one rack fetch may spend, across every instance it tries.
 *
 * One number rather than a per-attempt timeout that stacks. Walking four instances at ninety
 * seconds each meant a dead upstream cost six minutes per cell and the better part of half an
 * hour for a screenful — and web, which had no client timeout at all, never gave up.
 */
private val FetchDeadline = 30.seconds

/**
 * Asks several Overpass instances at once and takes the first real answer.
 *
 * Hedged rather than serial because "slow" and "down" look identical for the first few seconds,
 * and serial failover pays the full timeout to tell them apart. Starting the next instance while
 * the first is still thinking costs one extra request in the bad case and saves the whole timeout
 * in the common one. Different instances have separate per-address budgets, so racing them does
 * not spend any single instance's two slots faster than asking it once would.
 *
 * A mirror that is down fails in every shape there is — refused connection, timeout, an expired
 * certificate, a 502 whose HTML body the parser rejects — so anything short of a parseable
 * Overpass document counts as "let another one answer" rather than as an answer.
 *
 * When they all fail the last error travels rather than an empty list, because the caller has to
 * be able to tell a dead upstream from ground with no racks on it — see [ParkingRepositoryImpl],
 * which caches the second for a week.
 */
internal class OverpassEndpointRacer(
    private val endpoints: List<String> = OverpassEndpoints,
    private val health: EndpointHealth = EndpointHealth(),
    private val hedgeDelay: Duration = HedgeDelay,
    private val deadline: Duration = FetchDeadline,
) {
    suspend fun <T> race(attempt: suspend (endpoint: String) -> T): T {
        val candidates = order()
        if (candidates.isEmpty()) error("No Overpass endpoint is configured")
        var lastError: Throwable? = null
        return try {
            withTimeout(deadline) {
                coroutineScope {
                    val answers = Channel<Result<T>>(Channel.UNLIMITED)
                    // A token per finished attempt, so an instance that refuses outright starts
                    // the next one straight away instead of holding the race up for the hedge.
                    val finished = Channel<Unit>(Channel.UNLIMITED)
                    val permits = Semaphore(MaxConcurrentAttempts)
                    val attempts = launch {
                        candidates.forEachIndexed { position, endpoint ->
                            if (position > 0) withTimeoutOrNull(hedgeDelay) { finished.receive() }
                            launch {
                                val answer = permits.withPermit { tryOne(endpoint, attempt) }
                                finished.trySend(Unit)
                                answers.send(answer)
                            }
                        }
                    }
                    repeat(candidates.size) {
                        val answer = answers.receive()
                        answer.getOrNull()?.let { value ->
                            // Nothing is waiting on the losers, and a browser left fetching a
                            // body nobody will read still holds a connection.
                            attempts.cancel()
                            return@coroutineScope value
                        }
                        lastError = answer.exceptionOrNull()
                    }
                    throw lastError ?: IllegalStateException("No Overpass endpoint answered")
                }
            }
        } catch (timeout: TimeoutCancellationException) {
            // Deliberately not allowed to escape as a cancellation. Everything upstream of here
            // rethrows CancellationException untouched so a cancelled screen stays cancelled —
            // so a deadline that travelled as one would read as "the caller went away", skip the
            // stale-cache fallback in ParkingRepositoryImpl, and never reach the UI as an error.
            throw IllegalStateException("No Overpass endpoint answered within $deadline", lastError ?: timeout)
        }
    }

    private suspend fun <T> tryOne(
        endpoint: String,
        attempt: suspend (String) -> T,
    ): Result<T> =
        try {
            Result.success(attempt(endpoint)).also { health.recordSuccess(endpoint) }
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (error: Throwable) {
            health.recordFailure(endpoint)
            Result.failure(error)
        }

    /**
     * The instances to ask, best first, with the ones that have just refused us left out.
     *
     * The configured order is kept rather than shuffled: the main instance is the reference
     * implementation and the best synced, so the answer it gives is the one worth having, and
     * hedging already means a slow one costs [hedgeDelay] rather than a whole timeout. Spreading
     * load is [EndpointHealth]'s job, and it does it where it actually matters — an instance that
     * has refused twice drops out for everything that follows.
     *
     * Every instance being in cooldown falls back to asking them all: a stale opinion about who is
     * down is a worse answer than a request that might work.
     */
    private suspend fun order(): List<String> = health.usable(endpoints).ifEmpty { endpoints }
}
